import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { AutomaticGainControlChannel } from '../../api/models';
import { AutomaticGainControlApiService } from '../../api/services';
import {
	AutomaticGainControlChannelMutationGQL,
	AutomaticGainControlChannelQueryGQL,
	AutomaticGainControlChannelSubscriptionGQL
} from '../graphql/generated/automatic-gain-control.generated';
import { mapAgcChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspAgcService implements AutomaticGainControlApiService {
	private readonly logger: ILogger;

	private readonly agcChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly automaticGainControlChannelQueryGQL: AutomaticGainControlChannelQueryGQL,
		private readonly automaticGainControlChannelMutationGQL: AutomaticGainControlChannelMutationGQL,
		automaticGainControlChannelSubscriptionGQL: AutomaticGainControlChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspAgcService');

		this.agcChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspAgcService',
				changeTypes: [
					NodeChangeType.AudioChannelAutomaticGainControl,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: automaticGainControlChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<AutomaticGainControlChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying agc channel', { channelId, queryId });

		return this.automaticGainControlChannelQueryGQL
			.watch({ channelId }, { fetchPolicy: 'network-only', errorPolicy: 'ignore' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$()', 'Query updated', { channelId });
						return mapAgcChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((channel) => this.agcChannelSubscriptionManager.subscribe(queryId, channel.id)),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.agcChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public toggleAgc(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAgc()', 'Toggling agc', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}

	public setTargetLevel(channelId: string, targetLevel: number): Observable<Response<void, string>> {
		this.logger.trace('setTargetLevel()', 'Setting target level', { channelId, targetLevel });
		return this.updateChannel(channelId, { targetLevel });
	}

	public setMaxBoost(channelId: string, maxBoost: number): Observable<Response<void, string>> {
		this.logger.trace('setMaxBoost()', 'Setting max boost', { channelId, maxBoost });
		return this.updateChannel(channelId, { maxBoost });
	}

	public setMaxCut(channelId: string, maxCut: number): Observable<Response<void, string>> {
		this.logger.trace('setMaxCut()', 'Setting max cut', { channelId, maxCut });
		return this.updateChannel(channelId, { maxCut });
	}

	private updateChannel(
		channelId: string,
		update: Partial<AutomaticGainControlChannel['features']['automaticGainControl']>
	): Observable<Response<void, string>> {
		return this.automaticGainControlChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								automaticGainControl: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}
}
